import { BloomPass } from "./bloom";
import { imageControls } from "./image-controls";
import { MotionBlurPass } from "./motion-blur";
import { buildSpriteAtlas, type SpriteAtlas } from "./sprite-atlas";
import arcWGSL from "./shaders/arc.wgsl?raw";
import heatGlowWGSL from "./shaders/heat-glow.wgsl?raw";
import starfieldWGSL from "./shaders/starfield.wgsl?raw";
import tilesWGSL from "./shaders/tiles.wgsl?raw";
import tonemapWGSL from "./shaders/tonemap.wgsl?raw";
import { vizState } from "./viz-mode";

const HDR_FORMAT: GPUTextureFormat = "rgba16float";
const VEL_FORMAT: GPUTextureFormat = "rg16float";

const SUPERSAMPLE = 1;

/** = tonemap.wgsl Controls. */
const TONEMAP_UBO_BYTES = 32;

/** = tiles.wgsl Instance. */
const INSTANCE_FLOATS = 17;
const INSTANCE_BYTES = INSTANCE_FLOATS * 4;
/** Initial per-frame capacities. Buffers grow (double) on demand in push*(),
 *  so a dense scene (e.g. a large asteroid belt) is never silently culled. */
const INIT_INSTANCES = 8192;
const INIT_PLUME_INSTANCES = 4096;
/** = WGSL Camera (24 floats: 12 base + viz vec4 + vel/angVel/shutter + starParams vec4). */
const CAM_UBO_BYTES = 96;

const MOTION_BLUR_SHUTTER_S = 1 / 60;

export interface CameraState {
  /** Continuous absolute world position; entity coords share this unbounded
   *  frame (float64 keeps it precise, the GPU only sees cam-relative deltas). */
  x: number;
  y: number;
  rot: number;
  worldW: number;
  worldH: number;
  time: number;
  vx: number;
  vy: number;
  /** Aligned mode: followed ship's angVel. Normal: 0. */
  angVel: number;
  /** sqrt(zoom/8) — starfield.wgsl scales sprite radius and brightness
   *  by this so background stars shrink to ~71% per 2× zoom-out. */
  starScale: number;
}

interface TileInstance {
  cx: number;
  cy: number;
  sw: number;
  sh: number;
  rot: number;
  flags: number;
  u: number;
  v: number;
  uw: number;
  uh: number;
  tempK: number;
  damageFrac: number;
  emissivity?: number;
  /** Caller adds ω×r so each module on a spinning ship gets local lin vel. */
  velX?: number;
  velY?: number;
  angVel?: number;
  /** Stable per-block [0,1) phase seed — drives per-instance offsets
      in the arc / spark hashes so blocks don't blink in lockstep.
      Defaults to 0; tiles that don't host the destruction overlay
      can ignore it. */
  seed?: number;
}

export class Renderer {
  readonly device: GPUDevice;
  readonly context: GPUCanvasContext;
  readonly canvas: HTMLCanvasElement;
  readonly format: GPUTextureFormat;
  readonly atlas: SpriteAtlas;

  pixelW = 0;
  pixelH = 0;

  /** float64; pushTile() subtracts these so GPU sees cam-relative coords. */
  private camX = 0;
  private camY = 0;

  /** ORed into every pushed flag (= tiles.wgsl FLAG_DIM); reset at endFrame. */
  private tileFlagMask = 0;

  private damageUV = { u: 0, v: 0, w: 0, h: 0 };

  private camUBO: GPUBuffer;
  private instanceBuf: GPUBuffer;
  private plumeInstanceBuf: GPUBuffer;

  private starPipeline!: GPURenderPipeline;
  private starBind!: GPUBindGroup;

  private tilePipeline!: GPURenderPipeline;
  private plumePipeline!: GPURenderPipeline;
  private tileBind!: GPUBindGroup;

  private hdrTexture: GPUTexture | null = null;
  private hdrView: GPUTextureView | null = null;
  private hdrW = 0;
  private hdrH = 0;

  private tonemapPipeline!: GPURenderPipeline;
  private tonemapLayout!: GPUBindGroupLayout;
  private tonemapSampler!: GPUSampler;
  private tonemapUBO!: GPUBuffer;
  private tonemapBind: GPUBindGroup | null = null;

  private bloom!: BloomPass;
  private motionBlur!: MotionBlurPass;

  private instanceScratch = new Float32Array(INIT_INSTANCES * INSTANCE_FLOATS);
  private instanceCap = INIT_INSTANCES;
  private instanceCount = 0;
  private plumeScratch = new Float32Array(INIT_PLUME_INSTANCES * INSTANCE_FLOATS);
  private plumeCap = INIT_PLUME_INSTANCES;
  private plumeCount = 0;

  static async create(canvas: HTMLCanvasElement): Promise<Renderer> {
    if (!("gpu" in navigator)) {
      throw new Error("WebGPU not available (navigator.gpu missing).");
    }
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) throw new Error("No GPU adapter available.");
    const device = await adapter.requestDevice();
    const context = canvas.getContext("webgpu");
    if (!context) throw new Error("Could not get webgpu context.");
    const format = navigator.gpu.getPreferredCanvasFormat();
    context.configure({ device, format, alphaMode: "opaque" });

    const atlas = await buildSpriteAtlas(device);
    return new Renderer(device, context, canvas, format, atlas);
  }

  private constructor(
    device: GPUDevice,
    context: GPUCanvasContext,
    canvas: HTMLCanvasElement,
    format: GPUTextureFormat,
    atlas: SpriteAtlas,
  ) {
    this.device = device;
    this.context = context;
    this.canvas = canvas;
    this.format = format;
    this.atlas = atlas;
    const dmg = atlas.lookup.get("rock_damage");
    if (dmg) this.damageUV = { u: dmg.u, v: dmg.v, w: dmg.w, h: dmg.h };

    this.camUBO = device.createBuffer({
      label: "camera ubo",
      size: CAM_UBO_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.instanceBuf = device.createBuffer({
      label: "tile instances",
      size: INIT_INSTANCES * INSTANCE_BYTES,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });

    this.plumeInstanceBuf = device.createBuffer({
      label: "plume instances",
      size: INIT_PLUME_INSTANCES * INSTANCE_BYTES,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });

    this.tonemapUBO = device.createBuffer({
      label: "tonemap controls",
      size: TONEMAP_UBO_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    this.createStarPipeline();
    this.createTilePipeline();
    this.createTonemapPipeline();
    this.bloom = new BloomPass(device);
    this.motionBlur = new MotionBlurPass(device);
  }

  private createStarPipeline(): void {
    const shader = this.device.createShaderModule({ label: "starfield", code: heatGlowWGSL + "\n" + starfieldWGSL });
    const layout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
      ],
    });
    this.starPipeline = this.device.createRenderPipeline({
      label: "starfield",
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          { format: HDR_FORMAT },
          { format: VEL_FORMAT },
        ],
      },
      primitive: { topology: "triangle-list" },
    });
    this.starBind = this.device.createBindGroup({
      layout,
      entries: [{ binding: 0, resource: { buffer: this.camUBO } }],
    });
  }

  private createTilePipeline(): void {
    const shader = this.device.createShaderModule({ label: "tiles", code: heatGlowWGSL + "\n" + arcWGSL + "\n" + tilesWGSL });
    const layout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
      ],
    });
    const pipelineLayout = this.device.createPipelineLayout({ bindGroupLayouts: [layout] });
    const vertexBuffers: GPUVertexBufferLayout[] = [
      {
        arrayStride: INSTANCE_BYTES,
        stepMode: "instance",
        attributes: [
          { shaderLocation: 0,  offset: 0,  format: "float32x2" },
          { shaderLocation: 1,  offset: 8,  format: "float32x2" },
          { shaderLocation: 2,  offset: 16, format: "float32"   },
          { shaderLocation: 3,  offset: 20, format: "float32"   },
          { shaderLocation: 4,  offset: 24, format: "float32x2" },
          { shaderLocation: 5,  offset: 32, format: "float32x2" },
          { shaderLocation: 6,  offset: 40, format: "float32"   },
          { shaderLocation: 7,  offset: 44, format: "float32"   },
          { shaderLocation: 8,  offset: 48, format: "float32"   },
          { shaderLocation: 9,  offset: 52, format: "float32x2" },
          { shaderLocation: 10, offset: 60, format: "float32"   },
          { shaderLocation: 11, offset: 64, format: "float32"   },
        ],
      },
    ];
    this.tilePipeline = this.device.createRenderPipeline({
      label: "tiles",
      layout: pipelineLayout,
      vertex: { module: shader, entryPoint: "vs_main", buffers: vertexBuffers },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          {
            format: HDR_FORMAT,
            blend: {
              color: { srcFactor: "src-alpha", dstFactor: "one-minus-src-alpha" },
              alpha: { srcFactor: "one",       dstFactor: "one-minus-src-alpha" },
            },
          },
          { format: VEL_FORMAT },
        ],
      },
      primitive: { topology: "triangle-list" },
    });
    // Velocity write-masked off → plumes inherit emitter velocity.
    this.plumePipeline = this.device.createRenderPipeline({
      label: "plume",
      layout: pipelineLayout,
      vertex: { module: shader, entryPoint: "vs_main", buffers: vertexBuffers },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [
          {
            format: HDR_FORMAT,
            blend: {
              color: { srcFactor: "src-alpha", dstFactor: "one" },
              alpha: { srcFactor: "one",       dstFactor: "one" },
            },
          },
          { format: VEL_FORMAT, writeMask: 0 },
        ],
      },
      primitive: { topology: "triangle-list" },
    });
    this.tileBind = this.device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: { buffer: this.camUBO } },
        { binding: 1, resource: this.atlas.view },
        { binding: 2, resource: this.atlas.sampler },
      ],
    });
  }

  private createTonemapPipeline(): void {
    const shader = this.device.createShaderModule({ label: "tonemap", code: tonemapWGSL });
    const layout = this.device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
      ],
    });
    this.tonemapLayout = layout;
    this.tonemapPipeline = this.device.createRenderPipeline({
      label: "tonemap",
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_main",
        targets: [{ format: this.format }],
      },
      primitive: { topology: "triangle-list" },
    });
    this.tonemapSampler = this.device.createSampler({
      label: "hdr sampler",
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge",
    });
  }

  private ensureHdrTarget(): void {
    const targetW = this.pixelW * SUPERSAMPLE;
    const targetH = this.pixelH * SUPERSAMPLE;
    if (this.hdrTexture && this.hdrW === targetW && this.hdrH === targetH) return;
    if (this.hdrTexture) this.hdrTexture.destroy();
    this.hdrTexture = this.device.createTexture({
      label: "hdr scene",
      size: { width: targetW, height: targetH },
      format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.hdrView = this.hdrTexture.createView();
    this.hdrW = targetW;
    this.hdrH = targetH;
    // Bloom/tonemap read the blurred output; raw HDR feeds only motion blur.
    this.motionBlur.resize(targetW, targetH, this.hdrView);
    const sceneView = this.motionBlur.outputView();
    this.bloom.resize(targetW, targetH, sceneView);
    this.tonemapBind = this.device.createBindGroup({
      label: "tonemap bind",
      layout: this.tonemapLayout,
      entries: [
        { binding: 0, resource: sceneView },
        { binding: 1, resource: this.tonemapSampler },
        { binding: 2, resource: { buffer: this.tonemapUBO } },
        { binding: 3, resource: this.bloom.bloomView() },
      ],
    });
  }

  resizeToCanvas(): void {
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.max(1, Math.floor(this.canvas.clientWidth * dpr));
    const ch = Math.max(1, Math.floor(this.canvas.clientHeight * dpr));
    if (this.canvas.width !== cw || this.canvas.height !== ch) {
      this.canvas.width = cw;
      this.canvas.height = ch;
    }
    this.pixelW = cw;
    this.pixelH = ch;
  }

  beginFrame(cam: CameraState): void {
    this.instanceCount = 0;
    this.plumeCount = 0;
    this.tileFlagMask = 0;
    this.camX = cam.x;
    this.camY = cam.y;
    // Starfield hashes absolute coords; wrap to keep float32 precision.
    const WRAP = 1_000_000;
    const wx = cam.x - Math.round(cam.x / WRAP) * WRAP;
    const wy = cam.y - Math.round(cam.y / WRAP) * WRAP;
    const f32 = new Float32Array(CAM_UBO_BYTES / 4);
    f32[0] = wx;
    f32[1] = wy;
    f32[2] = cam.worldW;
    f32[3] = cam.worldH;
    f32[4] = this.pixelW * SUPERSAMPLE;
    f32[5] = this.pixelH * SUPERSAMPLE;
    f32[6] = cam.time;
    f32[7] = cam.rot;
    const d = this.damageUV;
    f32[8]  = d.u;
    f32[9]  = d.v;
    f32[10] = d.w;
    f32[11] = d.h;
    const vm = vizState.mode;
    f32[12] = vm.id;
    f32[13] = vm.rangeLo;
    f32[14] = vm.rangeHi;
    // Wall-clock seconds; cam.time stays bound to world.time.
    f32[15] = performance.now() * 0.001;
    f32[16] = cam.vx;
    f32[17] = cam.vy;
    f32[18] = cam.angVel;
    f32[19] = MOTION_BLUR_SHUTTER_S;
    f32[20] = cam.starScale;
    // f32[21..23] reserved
    this.device.queue.writeBuffer(this.camUBO, 0, f32);
  }

  /** ORed into every subsequent push until the next call or endFrame. */
  setTileFlagMask(mask: number): void {
    this.tileFlagMask = mask;
  }

  /** Double the tile-instance capacity (scratch + GPU buffer), preserving
   *  whatever's already been pushed this frame. */
  private growInstances(): void {
    this.instanceCap *= 2;
    const next = new Float32Array(this.instanceCap * INSTANCE_FLOATS);
    next.set(this.instanceScratch);
    this.instanceScratch = next;
    this.instanceBuf.destroy();
    this.instanceBuf = this.device.createBuffer({
      label: "tile instances",
      size: this.instanceCap * INSTANCE_BYTES,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
  }

  private growPlume(): void {
    this.plumeCap *= 2;
    const next = new Float32Array(this.plumeCap * INSTANCE_FLOATS);
    next.set(this.plumeScratch);
    this.plumeScratch = next;
    this.plumeInstanceBuf.destroy();
    this.plumeInstanceBuf = this.device.createBuffer({
      label: "plume instances",
      size: this.plumeCap * INSTANCE_BYTES,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
    });
  }

  pushTile(t: TileInstance): void {
    this.pushTileRaw(
      t.cx, t.cy, t.sw, t.sh, t.rot, t.flags,
      t.u, t.v, t.uw, t.uh, t.tempK, t.damageFrac,
      t.emissivity ?? 1.0,
      t.velX ?? 0, t.velY ?? 0, t.angVel ?? 0,
      t.seed ?? 0,
    );
  }

  pushTileRaw(
    cx: number, cy: number,
    sw: number, sh: number,
    rot: number, flags: number,
    u: number, v: number, uw: number, uh: number,
    tempK: number, damageFrac: number,
    emissivity: number,
    velX: number, velY: number, angVel: number,
    seed: number = 0,
  ): void {
    if (this.instanceCount >= this.instanceCap) this.growInstances();
    const off = this.instanceCount * INSTANCE_FLOATS;
    const a = this.instanceScratch;
    a[off + 0] = cx - this.camX;
    a[off + 1] = cy - this.camY;
    a[off + 2] = sw;
    a[off + 3] = sh;
    a[off + 4] = rot;
    a[off + 5] = flags | this.tileFlagMask;
    a[off + 6] = u;
    a[off + 7] = v;
    a[off + 8] = uw;
    a[off + 9] = uh;
    a[off + 10] = tempK;
    a[off + 11] = damageFrac;
    a[off + 12] = emissivity;
    a[off + 13] = velX;
    a[off + 14] = velY;
    a[off + 15] = angVel;
    a[off + 16] = seed;
    this.instanceCount++;
  }

  pushPlume(t: TileInstance): void {
    this.pushPlumeRaw(t.cx, t.cy, t.sw, t.sh, t.rot, t.flags, t.u, t.v, t.uw, t.uh, t.tempK, t.damageFrac);
  }

  pushPlumeRaw(
    cx: number, cy: number,
    sw: number, sh: number,
    rot: number, flags: number,
    u: number, v: number, uw: number, uh: number,
    tempK: number, damageFrac: number,
  ): void {
    if (this.plumeCount >= this.plumeCap) this.growPlume();
    const off = this.plumeCount * INSTANCE_FLOATS;
    const a = this.plumeScratch;
    a[off + 0] = cx - this.camX;
    a[off + 1] = cy - this.camY;
    a[off + 2] = sw;
    a[off + 3] = sh;
    a[off + 4] = rot;
    a[off + 5] = flags | this.tileFlagMask;
    a[off + 6] = u;
    a[off + 7] = v;
    a[off + 8] = uw;
    a[off + 9] = uh;
    a[off + 10] = tempK;
    a[off + 11] = damageFrac;
    a[off + 12] = 1.0;
    // Plumes early-out before motion blur (FLAG_PLUME_RAY → capsule SDF).
    a[off + 13] = 0;
    a[off + 14] = 0;
    a[off + 15] = 0;
    a[off + 16] = 0;
    this.plumeCount++;
  }

  endFrame(): void {
    this.ensureHdrTarget();

    const ic = imageControls;
    const tm = new Float32Array(TONEMAP_UBO_BYTES / 4);
    tm[0] = ic.reinhardWhite;
    tm[1] = ic.sigmoidMidpoint;
    tm[2] = ic.sigmoidSteepness;
    tm[3] = ic.bloomStrength;
    tm[4] = ic.exposure;
    this.device.queue.writeBuffer(this.tonemapUBO, 0, tm);

    if (this.instanceCount > 0) {
      this.device.queue.writeBuffer(
        this.instanceBuf,
        0,
        this.instanceScratch.buffer,
        0,
        this.instanceCount * INSTANCE_BYTES,
      );
    }
    if (this.plumeCount > 0) {
      this.device.queue.writeBuffer(
        this.plumeInstanceBuf,
        0,
        this.plumeScratch.buffer,
        0,
        this.plumeCount * INSTANCE_BYTES,
      );
    }

    const enc = this.device.createCommandEncoder({ label: "frame" });

    const pass = enc.beginRenderPass({
      colorAttachments: [
        {
          view: this.hdrView!,
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0.02, g: 0.024, b: 0.035, a: 1 },
        },
        {
          // Velocity: cleared to zero so alpha-discarded pixels skip blur.
          view: this.motionBlur.velocityView(),
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 0 },
        },
      ],
    });
    pass.setPipeline(this.starPipeline);
    pass.setBindGroup(0, this.starBind);
    pass.draw(3);

    if (this.instanceCount > 0) {
      pass.setPipeline(this.tilePipeline);
      pass.setBindGroup(0, this.tileBind);
      pass.setVertexBuffer(0, this.instanceBuf);
      pass.draw(6, this.instanceCount);
    }

    // Plumes after tiles: brightest 0-5% of cone would otherwise be hidden.
    if (this.plumeCount > 0) {
      pass.setPipeline(this.plumePipeline);
      pass.setBindGroup(0, this.tileBind);
      pass.setVertexBuffer(0, this.plumeInstanceBuf);
      pass.draw(6, this.plumeCount);
    }
    pass.end();

    this.motionBlur.encode(enc, ic.motionBlurAmount);

    this.bloom.encode(enc, ic.bloomThreshold, ic.bloomScatter);

    const swapView = this.context.getCurrentTexture().createView();
    const tonePass = enc.beginRenderPass({
      colorAttachments: [
        {
          view: swapView,
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        },
      ],
    });
    tonePass.setPipeline(this.tonemapPipeline);
    tonePass.setBindGroup(0, this.tonemapBind!);
    tonePass.draw(3);
    tonePass.end();

    this.device.queue.submit([enc.finish()]);
  }
}
